import { computed, onMounted, ref, type Ref } from 'vue';
import { toast } from '@flash-global66/g-toast';
import { openAlert } from '@flash-global66/g-dialog-alert';

// TYPES
import type { CurrencyWalletType } from '@/types/CurrencyWalletType';

// COMPOSABLES
import useLoader from '@/composables/useLoader';

// STORES
import { useWalletStore } from '@/stores/wallet';
import { useAccountStore } from '@/stores/account';

// SHARED
import { getReasonMessage, walletService } from '@/shared/common';
import { WALLET_STABLECOINS_V3_CURRENCIES } from '@/shared/common/constants/walletStablecoinsV3.constant';
import { useRemoteConfigStore } from '@/shared/firebase';
import { useCanAccessWalletStablecoinsV3 } from '@/shared/firebase/composables/useCanAccessWalletStablecoinsV3';

// HELPERS
import { canCreateStablecoinWallet } from '@/helpers/kyc';

export function useCreateWalletCurrency() {
  const { showGLoader } = useLoader();
  const walletStore = useWalletStore();
  const accountStore = useAccountStore();
  const remoteConfigStore = useRemoteConfigStore();
  const canAccessWalletStablecoinsV3 = useCanAccessWalletStablecoinsV3();

  const currenciesWallet: Ref<CurrencyWalletType[]> = ref([]);
  const generalFlag = remoteConfigStore.getFeatureFlag('feature_wallet_stablecoins');
  const institutionalConfig = remoteConfigStore.getRemoteConfigValue(
    'feature_wallet_stablecoins_institutional'
  );

  const isStablecoinEnabled = computed<boolean>(() =>
    canCreateStablecoinWallet({
      isInstitutional: Boolean(accountStore.kycStatus?.company?.institutional),
      companyId: String(accountStore.kycStatus?.company?.id ?? ''),
      generalEnabled: generalFlag.value.enabled,
      institutionalEnabled: institutionalConfig.value.enabled,
      institutionalWhitelist: institutionalConfig.value.whitelist,
    })
  );

  /**
   * Se filtra por código y no por `isStablecoin`: el backend puede marcar BTC/ETH
   * con cualquiera de los dos valores y en ambos casos deben ocultarse sin acceso v3.
   */
  const visibleCurrencies = computed(() =>
    currenciesWallet.value.filter(
      (c) => canAccessWalletStablecoinsV3.value || !WALLET_STABLECOINS_V3_CURRENCIES.has(c.currencyCode)
    )
  );

  const localCurrencies = computed(() =>
    visibleCurrencies.value.filter((c) => !c.isStablecoin)
  );

  const stablecoinCurrencies = computed(() =>
    visibleCurrencies.value.filter((c) => c.isStablecoin)
  );

  /**
   * Mismo criterio que el template: la sección Stablecoins solo se pinta con
   * `isStablecoinEnabled`. Sin nada que pintar, el drawer quedaría en blanco.
   */
  const hasSelectableCurrencies = computed<boolean>(
    () =>
      localCurrencies.value.length > 0 ||
      (isStablecoinEnabled.value && stablecoinCurrencies.value.length > 0)
  );

  async function getCurrenciesWallet() {
    showGLoader(true);
    const result = await walletService.getWalletCurrencies();

    let hasNoCurrencies = false;

    result.fold(
      (failure) => {
        const errorMessage = getReasonMessage({
          failure,
          defaultMessage: 'Ocurrió un error al cargar las monedas.',
        });
        toast.error(errorMessage);
        currenciesWallet.value = [];
      },
      (success) => {
        currenciesWallet.value = success.data;
        hasNoCurrencies = !hasSelectableCurrencies.value;
      }
    );

    showGLoader(false);

    if (hasNoCurrencies) {
      await openAlert({
        type: 'info',
        title: 'Importante',
        description: 'Por el momento no tenemos más billeteras para crear',
        primaryText: 'Continuar',
      });
      walletStore.closeCreateWalletDrawer();
    }
  }

  onMounted(async () => {
    await getCurrenciesWallet();
  });

  return {
    isStablecoinEnabled,
    localCurrencies,
    stablecoinCurrencies,
  };
}
